import { format, isBefore, isSameDay, isValid, setSeconds } from 'date-fns';
import { RegisterOptions, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
// バリデーションルール
export const useValidSaveButton = (methods: UseFormReturn) => {
  const { t } = useTranslation();

  // 項目「アンケート」の必須チェック
  const rulesSurvey = {
    required: { value: true, message: t('enqueteCalendarList.message.requiredSurvey') },
  };

  // カレンダー適用開始日バリデーションルール
  const startDateRule: RegisterOptions = {
    required: {
      value: true,
      // エラーメッセージ： 適用開始日を入力してください
      message: t('CalendarCreate.validateError.startDateRequired'),
    },
    validate: {
      isValidDate: (value: string) => {
        if (!isValid(new Date(value))) {
          // エラーメッセージ： 日付の形式が正しくありません
          return `${t('CalendarCreate.validateError.dateFormat')}`;
        }
        return true;
      },
      isValidPeriod: (value: string) => {
        const startDate = new Date(value);
        const endDate = setSeconds(methods.getValues().endDate, 0);
        // 終了日が未入力の場合は、バリデーションを実行する
        if (!isValid(endDate) && startDate) {
          methods.setError('endDate', {
            type: 'validate',
            message: t('CalendarCreate.validateError.endDateRequired'),
          });
          return true;
        }
        if (isPastDate(startDate, endDate)) {
          // 相関チェックエラーのみクリアする
          if (methods.getFieldState('endDate').error?.type === 'isValidPeriod') {
            methods.clearErrors('endDate');
          }
          return true;
        } else {
          // エラーメッセージ： 開始日は終了日よりも前に設定してください
          return `${t('CalendarCreate.message.validationErrorInputFromIsValidFutureDate')}`;
        }
      },
    },
  };

  // カレンダー適用終了日バリデーションルール
  const endDateRule: RegisterOptions = {
    required: {
      value: true,
      // エラーメッセージ： 適用終了日を入力してください
      message: t('CalendarCreate.validateError.endDateRequired'),
    },
    validate: {
      isValidDate: (value: string) => {
        if (!isValid(new Date(value))) {
          // エラーメッセージ： 日付の形式が正しくありません
          return `${t('CalendarCreate.validateError.dateFormat')}`;
        }
        return true;
      },
      isValidPeriod: (value: string) => {
        const startDate = setSeconds(methods.getValues().startDate, 0);
        const endDate = new Date(value);
        // 開始日が未入力の場合は、バリデーションを実行する
        if (!isValid(startDate) && endDate) {
          methods.setError('startDate', {
            type: 'validate',
            message: t('CalendarCreate.validateError.startDateRequired'),
          });
          return true;
        }
        if (isPastDate(startDate, endDate)) {
          // 相関チェックエラーのみクリアする
          if (methods.getFieldState('startDate').error?.type === 'isValidPeriod') {
            methods.clearErrors('startDate');
          }
          return true;
        } else {
          // エラーメッセージ： 終了日付は開始日付より後に設定してください
          return `${t('CalendarCreate.message.validationErrorInputFromIsValidFutureDate')}`;
        }
      },
    },
  };

  /**
   * 比較先が設定されていない or 比較元が比較先の過去の日付または同日の場合true、違う場合falseを返却する
   * @param source 比較元
   * @param destination 比較先
   * @returns 比較先が設定されていない or 比較元が比較先の過去の日付の場合True
   */
  const isPastDate = (source: Date, destination: Date) => {
    const startDate = format(source, 'yyyy/MM/dd');
    const endDate = format(destination, 'yyyy/MM/dd');

    // 文字列比較だが、フォーマット、文字数が同じため問題なし
    return startDate <= endDate;
  };

  // リマインド日付バリデーションルール
  const reminderDateRule: RegisterOptions = {
    required: {
      value: true,
      // エラーメッセージ： リマインド日付を入力してください
      message: t('CalendarCreate.validateError.reminderDateRequired'),
    },
    validate: {
      isValidDate: (value: string) => {
        if (!isValid(new Date(value))) {
          // エラーメッセージ： 日付の形式が正しくありません
          return `${t('CalendarCreate.validateError.dateFormat')}`;
        }
        // 過去日かつ本日と同じ日付でないこと
        if (isBefore(new Date(value), new Date()) || isSameDay(new Date(value), new Date())) {
          // エラーメッセージ： 現在日付以降の日付を入力してください
          return `${t('CalendarCreate.message.dateErrorMessage')}`;
        }
        return true;
      },
    },
  };

  return {
    // 項目「アンケート」の必須チェック
    rulesSurvey,
    // カレンダー適用開始日バリデーションルール
    startDateRule,
    // カレンダー適用終了日バリデーションルール
    endDateRule,
    // リマインド日付バリデーションルール
    reminderDateRule,
  };
};
